import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// ⓘ About this place (Nina 09-25, PORTED_HOME_HANDOFF §7): in a ported room the
// space's description leaves the band under the stage (one italic line, cut
// off mid-word) and lives behind an ⓘ beside the title. The band's own ⓘ was
// the ONLY door to Space Details (tour re-run, ghost lane, share), so this card
// keeps a link to it. Printed app text: the description is the space's, not
// the resident's words.
//
// The card is portaled to <body>: the title block clips (overflow hidden), and
// a fixed card follows the ⓘ on scroll and resize instead of closing, since the
// chat feed scrolls itself whenever a message lands.
// Class names avoid every substring the generated themes target
// ([class*='card'|'panel'|'button'|'btn'|'popover'|'tooltip'|'header'...]).

// The port writes "{name}'s home space. " + the persona; the title right above
// already says whose place it is.
export function aboutText(scene) {
  const s = String(scene || '').trim();
  const m = s.match(/^[^.\n]{1,80}['’]s home space\.\s*/);
  const rest = m ? s.slice(m[0].length).trim() : s;
  return rest || s;
}

const CSS = `
.pa-sheet, .pa-sheet * { box-sizing: border-box; }
.pa-sheet { position: fixed; z-index: 100020; padding: 12px 14px 10px; border-radius: 12px; background: #120E18; color: #EEE7F5; border: 1px solid rgba(238,231,245,0.16); box-shadow: 0 14px 34px rgba(0,0,0,0.55); text-align: left; animation: paIn .16s ease-out; }
.pa-sheet::before { content: ''; position: absolute; top: -6px; left: var(--pa-arrow, 50%); width: 10px; height: 10px; margin-left: -5px; background: #120E18; border-left: 1px solid rgba(238,231,245,0.16); border-top: 1px solid rgba(238,231,245,0.16); transform: rotate(45deg); }
.pa-text { font: 15px/1.5 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; overflow-wrap: anywhere; max-height: 50vh; overflow-y: auto; }
.pa-row { display: flex; justify-content: flex-end; margin-top: 8px; }
.pa-sheet .pa-more { all: unset; cursor: pointer; font: 600 12px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; color: rgba(238,231,245,0.72) !important; padding: 8px 2px !important; background: none !important; border: 0 !important; box-shadow: none !important; text-shadow: none !important; transform: none !important; }
.pa-sheet .pa-more:hover { color: #EEE7F5 !important; }
.pa-sheet .pa-more:focus-visible { outline: 2px solid #F0AC55; outline-offset: 2px; }
/* the ⓘ's armor (it sits in the place page's slim bar): themes set button padding/border/shadow with !important */
.pa-i.pa-i { box-sizing: border-box !important; padding: 0 !important; border: 1px solid rgba(238,231,245,0.35) !important; border-radius: 50% !important; color: rgba(238,231,245,0.85) !important; text-shadow: none !important; box-shadow: none !important; transform: none !important; min-width: 0 !important; min-height: 0 !important; }
.pa-i:focus-visible { outline: 2px solid #F0AC55 !important; outline-offset: 2px; }
@keyframes paIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .pa-sheet { animation: none; } }
`;

export default function PortedAbout({ text = '', onDetails = null, isMobile = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const sheetRef = useRef(null);
  const sheetId = `pa-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const place = useCallback(() => {
    const b = btnRef.current;
    if (!b || typeof window === 'undefined') return;
    const r = b.getBoundingClientRect();
    const vw = window.innerWidth || 0;
    const width = Math.max(200, Math.min(360, vw - 32));
    const mid = r.left + r.width / 2;
    const left = Math.max(16, Math.min(vw - 16 - width, mid - width / 2));
    setPos({ top: r.bottom + 10, left, width, arrow: Math.max(14, Math.min(width - 14, mid - left)) });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    let raf = 0;
    const follow = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(place); };
    const onDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (sheetRef.current && sheetRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      if (btnRef.current) btnRef.current.focus();
    };
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', follow, true);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  const size = isMobile ? 22 : 24;
  return (
    <>
      <style>{CSS}</style>
      <button
        ref={btnRef}
        type="button"
        data-prop-ui=""
        className="pa-i"
        aria-label="About this place"
        aria-expanded={open}
        aria-controls={open ? sheetId : undefined}
        onClick={() => setOpen((v) => !v)}
        style={{
          // both: --prop-bg is read only by the theme shield (a generated theme's
          // CSS); a port with no generated theme (D8) needs the inline background
          '--prop-bg': open ? 'rgba(238,231,245,0.14)' : 'rgba(10,8,16,0.35)',
          background: open ? 'rgba(238,231,245,0.14)' : 'rgba(10,8,16,0.35)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          verticalAlign: 'middle', flexShrink: 0,
          width: size + 10, height: size + 10, marginLeft: isMobile ? 6 : 10, padding: 0,
          borderRadius: '50%', border: '1px solid rgba(238,231,245,0.35)',
          color: 'rgba(238,231,245,0.85)', cursor: 'pointer',
          boxShadow: 'none', textShadow: 'none', transform: 'none',
        }}
      >
        <svg width={size - 6} height={size - 6} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </button>
      {open && pos && typeof document !== 'undefined' ? createPortal(
        <div
          ref={sheetRef}
          id={sheetId}
          className="pa-sheet"
          role="dialog"
          aria-label="About this place"
          style={{ top: pos.top, left: pos.left, width: pos.width, '--pa-arrow': `${pos.arrow}px` }}
        >
          {text ? <div className="pa-text">{text}</div> : null}
          {onDetails ? (
            <div className="pa-row">
              <button type="button" data-prop-ui="" className="pa-more"
                onClick={() => { setOpen(false); onDetails(); }}>Space details ›</button>
            </div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </>
  );
}
